/**
 * Aufgabenfilter — reine Funktionen, Suchparameter als Wahrheit.
 *
 * Suchparameter sind deutsch und kurz (Vertrag §2.4). Unbekannte Werte
 * führen zum Standard, nicht zum Fehler — ein geteilter Link darf nicht
 * kaputtgehen, weil ein Label gelöscht wurde.
 */

export type FristFilter = "ueberfaellig" | "heute" | "woche" | "ohne";
export type Sortierung = "frist" | "prioritaet" | "geaendert";

/** Wer-Filter: Person-UUID, `ich` oder `niemand`. */
export type WerFilter = string;

export type AufgabenFilter = {
  /** Mehrere Projekt-IDs; leer = alle sichtbaren. */
  projektIds: string[];
  /** Mehrere Zustands-IDs; leer = alle (bzw. UI-Standard offene). */
  zustandIds: string[];
  /** Erledigte und verworfene Aufgaben bewusst in die Sicht aufnehmen. */
  erledigteAnzeigen: boolean;
  /** Personen-ID, `ich` oder `niemand`; leer = alle. */
  wer: WerFilter[];
  /** Meilenstein-IDs oder `ohne`. */
  meilensteinIds: string[];
  /** Label-IDs. */
  labelIds: string[];
  frist?: FristFilter;
  sortierung: Sortierung;
  q?: string;
};

const FRIST_WERTE = new Set<string>([
  "ueberfaellig",
  "heute",
  "woche",
  "ohne",
]);
const SORT_WERTE = new Set<string>(["frist", "prioritaet", "geaendert"]);

function liste(
  roh: string | null | undefined,
): string[] {
  if (!roh?.trim()) return [];
  return [
    ...new Set(
      roh
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

function getParam(
  suche: URLSearchParams | Record<string, string | undefined>,
  key: string,
): string | undefined {
  if (suche instanceof URLSearchParams) {
    return suche.get(key) ?? undefined;
  }
  return suche[key];
}

/** Liest Filter aus URLSearchParams / Record. Unbekannt → Standard. */
export function filterAusSuche(
  suche: URLSearchParams | Record<string, string | undefined>,
): AufgabenFilter {
  const fristRoh = getParam(suche, "frist");
  const sortRoh = getParam(suche, "sortierung");
  const q = getParam(suche, "q")?.trim();

  return {
    projektIds: liste(getParam(suche, "projekt")),
    zustandIds: liste(getParam(suche, "zustand")),
    erledigteAnzeigen: getParam(suche, "erledigte") === "anzeigen",
    wer: liste(getParam(suche, "wer")),
    meilensteinIds: liste(getParam(suche, "meilenstein")),
    labelIds: liste(getParam(suche, "label")),
    ...(fristRoh && FRIST_WERTE.has(fristRoh)
      ? { frist: fristRoh as FristFilter }
      : {}),
    sortierung:
      sortRoh && SORT_WERTE.has(sortRoh)
        ? (sortRoh as Sortierung)
        : "frist",
    ...(q ? { q } : {}),
  };
}

/** Schreibt nur gesetzte Werte (für verlinkbare URLs). */
export function sucheAusFilter(
  filter: AufgabenFilter,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (filter.projektIds.length) out["projekt"] = filter.projektIds.join(",");
  if (filter.zustandIds.length) out["zustand"] = filter.zustandIds.join(",");
  if (filter.erledigteAnzeigen) out["erledigte"] = "anzeigen";
  if (filter.wer.length) out["wer"] = filter.wer.join(",");
  if (filter.meilensteinIds.length)
    out["meilenstein"] = filter.meilensteinIds.join(",");
  if (filter.labelIds.length) out["label"] = filter.labelIds.join(",");
  if (filter.frist) out["frist"] = filter.frist;
  if (filter.sortierung !== "frist") out["sortierung"] = filter.sortierung;
  if (filter.q) out["q"] = filter.q;
  return out;
}

/**
 * Welche Projekt-IDs die API laden muss.
 * Ohne Filter: alle sichtbaren; sonst Schnittmenge.
 */
export function filterZuEingabe(
  filter: AufgabenFilter,
  sichtbareProjektIds: readonly string[],
): { projektIds: string[] } {
  if (filter.projektIds.length === 0) {
    return { projektIds: [...sichtbareProjektIds] };
  }
  const erlaubt = new Set(sichtbareProjektIds);
  return {
    projektIds: filter.projektIds.filter((id) => erlaubt.has(id)),
  };
}

export type AufgabeZeile = {
  id: string;
  titel: string;
  inhalt?: string | null;
  zustandId?: string | null;
  meilensteinId?: string | null;
  prioritaet?: number | null;
  faelligAm?: Date | string | null;
  geaendertAm?: Date | string | null;
  projektId: string;
  /** Zugewiesene Personen-IDs (aus Kanten). */
  zugewiesenIds?: readonly string[];
  /** Label-IDs an der Aufgabe. */
  labelIds?: readonly string[];
  /** Gruppe aus `gruppeVon` — für Frist-Filter. */
  gruppe?: string;
  /** Fachlicher Abschluss aus der Zustandsgruppe. */
  fachlichOffen?: boolean;
};

/** Clientseitige Filter jenseits der API. */
export function filtereAufgaben<T extends AufgabeZeile>(
  zeilen: readonly T[],
  filter: AufgabenFilter,
  optionen: { personId?: string } = {},
): T[] {
  return zeilen.filter((z) => {
    if (
      z.fachlichOffen === false &&
      !filter.erledigteAnzeigen &&
      filter.zustandIds.length === 0
    ) {
      return false;
    }
    if (
      filter.zustandIds.length > 0 &&
      (!z.zustandId || !filter.zustandIds.includes(z.zustandId))
    ) {
      return false;
    }
    if (filter.meilensteinIds.length > 0) {
      const ohne = filter.meilensteinIds.includes("ohne");
      const ids = filter.meilensteinIds.filter((x) => x !== "ohne");
      const passtOhne = ohne && !z.meilensteinId;
      const passtId =
        z.meilensteinId != null && ids.includes(z.meilensteinId);
      if (!passtOhne && !passtId) return false;
    }
    if (filter.labelIds.length > 0) {
      const labels = z.labelIds ?? [];
      if (!filter.labelIds.some((l) => labels.includes(l))) return false;
    }
    if (filter.wer.length > 0) {
      const zug = z.zugewiesenIds ?? [];
      const passt = filter.wer.some((w) => {
        if (w === "niemand") return zug.length === 0;
        if (w === "ich")
          return optionen.personId != null && zug.includes(optionen.personId);
        return zug.includes(w);
      });
      if (!passt) return false;
    }
    if (filter.frist) {
      const g = z.gruppe;
      if (filter.frist === "ueberfaellig" && g !== "ueberfaellig") return false;
      if (filter.frist === "heute" && g !== "heute") return false;
      if (filter.frist === "woche" && g !== "diese_woche" && g !== "heute")
        return false;
      if (filter.frist === "ohne" && g !== "ohne_datum") return false;
    }
    if (filter.q) {
      const nadel = filter.q.toLowerCase();
      const heuhaufen = `${z.titel} ${z.inhalt ?? ""}`.toLowerCase();
      if (!heuhaufen.includes(nadel)) return false;
    }
    return true;
  });
}

function zeitMs(wert: Date | string | null | undefined): number {
  if (wert == null) return Number.POSITIVE_INFINITY;
  const t = wert instanceof Date ? wert.getTime() : new Date(wert).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

export function sortiereAufgaben<T extends AufgabeZeile>(
  zeilen: readonly T[],
  sortierung: Sortierung,
): T[] {
  const kopie = [...zeilen];
  kopie.sort((a, b) => {
    if (sortierung === "prioritaet") {
      return (b.prioritaet ?? 0) - (a.prioritaet ?? 0);
    }
    if (sortierung === "geaendert") {
      return zeitMs(b.geaendertAm) - zeitMs(a.geaendertAm);
    }
    // frist: frühere zuerst, ohne Datum ans Ende
    return zeitMs(a.faelligAm) - zeitMs(b.faelligAm);
  });
  return kopie;
}

export function leererFilter(): AufgabenFilter {
  return {
    projektIds: [],
    zustandIds: [],
    erledigteAnzeigen: false,
    wer: [],
    meilensteinIds: [],
    labelIds: [],
    sortierung: "frist",
  };
}

export function hatAktivenFilter(filter: AufgabenFilter): boolean {
  return (
    filter.projektIds.length > 0 ||
    filter.zustandIds.length > 0 ||
    filter.erledigteAnzeigen ||
    filter.wer.length > 0 ||
    filter.meilensteinIds.length > 0 ||
    filter.labelIds.length > 0 ||
    filter.frist != null ||
    Boolean(filter.q) ||
    filter.sortierung !== "frist"
  );
}
